// ═══════════════════════════════════════════════════════════════
// CAMPAIGN IMPORT MODAL — CSV Recipient Import Wizard
// Path: src/components/campaigns/CampaignImportModal.jsx
//
// Steps:
//   1. Upload CSV
//   2. Map columns + choose match strategy
//   3. Review matches
//   4. Execute import
//
// Reuses parseCSV from importService.js
// ═══════════════════════════════════════════════════════════════

import React, { useState, useCallback } from "react";
import {
  Upload,
  X,
  Check,
  AlertTriangle,
  FileText,
  ArrowRight,
} from "lucide-react";
import { useApp } from "../../context/AppContext";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../../constants/appConfig";
import { parseCSV } from "../../services/importService";
import { campaignService } from "../../services/campaignService";

const MEMBER_MATCH_FIELDS = [
  { value: "lastName", label: "Last Name" },
  { value: "firstName", label: "First Name" },
  { value: "email", label: "Email" },
  { value: "homeStreet", label: "Home Street" },
  { value: "homeCity", label: "Home City" },
  { value: "homeState", label: "Home State" },
  { value: "homeZip", label: "Home Zip" },
];

export const CampaignImportModal = ({ campaignId, onClose, onComplete }) => {
  const { palette, members, showMessage } = useApp();

  const [step, setStep] = useState(1);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [csvRows, setCsvRows] = useState([]);
  const [fileName, setFileName] = useState("");

  // Step 2 state
  const [strategy, setStrategy] = useState("spektrix_id");
  const [spektrixColumn, setSpektrixColumn] = useState("");
  const [compositeMapping, setCompositeMapping] = useState([
    { csvColumn: "", memberField: "lastName" },
  ]);

  // Step 3 state
  const [matchResult, setMatchResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  // ── Step 1: File upload ─────────────────────────────────────
  const handleFile = useCallback((event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const { headers, rows } = parseCSV(text);
      setCsvHeaders(headers);
      setCsvRows(rows);
      setStep(2);
    };
    reader.readAsText(file);
  }, []);

  // ── Step 2: Run matching ────────────────────────────────────
  const handleMatch = () => {
    let columnMapping = {};

    if (strategy === "spektrix_id") {
      if (!spektrixColumn) return;
      columnMapping = { [spektrixColumn]: "spektrixId" };
    } else {
      const valid = compositeMapping.filter((m) => m.csvColumn);
      if (valid.length === 0) return;
      valid.forEach((m) => {
        columnMapping[m.csvColumn] = m.memberField;
      });
    }

    const result = campaignService.matchRecipients(
      csvRows,
      columnMapping,
      strategy,
      members
    );
    setMatchResult(result);
    setStep(3);
  };

  // ── Step 3→4: Execute import ────────────────────────────────
  const handleImport = async () => {
    if (!matchResult || matchResult.matched.length === 0) return;
    setImporting(true);

    try {
      const memberIds = matchResult.matched.map((m) => m.memberId);
      // Deduplicate
      const unique = [...new Set(memberIds)];
      const count = await campaignService.addRecipients(
        campaignId,
        unique,
        strategy === "spektrix_id" ? "spektrix_id" : "composite"
      );
      setImportResult({ added: count, skipped: memberIds.length - unique.length });
      setStep(4);
      showMessage(`${unique.length} recipients imported`);
    } catch (err) {
      showMessage("Import failed", "error");
    } finally {
      setImporting(false);
    }
  };

  // ── Composite mapping helpers ───────────────────────────────
  const addMappingRow = () => {
    setCompositeMapping((prev) => [...prev, { csvColumn: "", memberField: "firstName" }]);
  };

  const updateMapping = (index, field, value) => {
    setCompositeMapping((prev) =>
      prev.map((m, i) => (i === index ? { ...m, [field]: value } : m))
    );
  };

  const removeMappingRow = (index) => {
    setCompositeMapping((prev) => prev.filter((_, i) => i !== index));
  };

  // ── Render ──────────────────────────────────────────────────
  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 1000,
          background: "rgba(0,0,0,0.4)",
        }}
      />

      {/* Modal */}
      <div
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 600,
          maxHeight: "80vh",
          zIndex: 1001,
          background: palette.cardBg,
          border: `1px solid ${palette.cardBorder}`,
          borderRadius: 12,
          boxShadow: "0 16px 48px rgba(0,0,0,0.2)",
          display: "flex",
          flexDirection: "column",
          fontFamily: FONT_FAMILY,
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "16px 20px",
            borderBottom: `1px solid ${palette.cardBorder}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Upload size={ICON_SIZES.sm} style={{ color: palette.text1 }} />
            <span style={{ fontSize: FONT_SIZES.sm, fontWeight: 700, color: palette.text1 }}>
              Import Recipients — Step {step} of 4
            </span>
          </div>
          <button
            onClick={step === 4 ? onComplete : onClose}
            style={{ background: "none", border: "none", cursor: "pointer", padding: 4, display: "flex" }}
          >
            <X size={ICON_SIZES.sm} style={{ color: palette.text3 }} />
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "20px" }}>
          {/* ── STEP 1: Upload ─────────────────────────────── */}
          {step === 1 && (
            <div style={{ textAlign: "center", padding: 20 }}>
              <FileText size={ICON_SIZES.xxl} style={{ color: palette.text3, marginBottom: 16 }} />
              <div style={{ fontSize: FONT_SIZES.xs, color: palette.text2, marginBottom: 16 }}>
                Upload a CSV file containing your mailing list.
              </div>
              <label
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "10px 20px",
                  borderRadius: 8,
                  background: palette.accent1 || "#3b82f6",
                  color: "#fff",
                  fontSize: FONT_SIZES.xs,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                <Upload size={ICON_SIZES.sm} />
                Choose File
                <input
                  type="file"
                  accept=".csv,.txt"
                  onChange={handleFile}
                  style={{ display: "none" }}
                />
              </label>
            </div>
          )}

          {/* ── STEP 2: Map + Strategy ─────────────────────── */}
          {step === 2 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div style={{ fontSize: FONT_SIZES.xs, color: palette.text2 }}>
                <strong>{fileName}</strong> — {csvRows.length} rows, {csvHeaders.length} columns
              </div>

              {/* Strategy picker */}
              <div>
                <div style={{ fontSize: "0.6rem", fontWeight: 600, color: palette.text2, marginBottom: 8 }}>
                  Match Strategy
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  {[
                    { id: "spektrix_id", label: "SpektrixID" },
                    { id: "composite", label: "Composite Key" },
                  ].map((s) => (
                    <button
                      key={s.id}
                      onClick={() => setStrategy(s.id)}
                      style={{
                        padding: "6px 14px",
                        borderRadius: 6,
                        border: `1px solid ${strategy === s.id ? palette.accent1 || "#3b82f6" : palette.inputBorder}`,
                        background: strategy === s.id ? `${palette.accent1 || "#3b82f6"}15` : "transparent",
                        color: strategy === s.id ? palette.accent1 || "#3b82f6" : palette.text2,
                        fontWeight: 600,
                        fontSize: FONT_SIZES.xs,
                        fontFamily: FONT_FAMILY,
                        cursor: "pointer",
                      }}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* SpektrixID mapping */}
              {strategy === "spektrix_id" && (
                <div>
                  <div style={{ fontSize: "0.6rem", fontWeight: 600, color: palette.text2, marginBottom: 4 }}>
                    CSV column containing SpektrixID
                  </div>
                  <select
                    value={spektrixColumn}
                    onChange={(e) => setSpektrixColumn(e.target.value)}
                    style={{
                      padding: "8px 12px",
                      borderRadius: 6,
                      fontSize: FONT_SIZES.xs,
                      fontFamily: FONT_FAMILY,
                      border: `1px solid ${palette.inputBorder}`,
                      background: palette.inputBg,
                      color: palette.inputColor,
                      outline: "none",
                      minWidth: 200,
                    }}
                  >
                    <option value="">Select column...</option>
                    {csvHeaders.map((h) => (
                      <option key={h} value={h}>{h}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Composite mapping */}
              {strategy === "composite" && (
                <div>
                  <div style={{ fontSize: "0.6rem", fontWeight: 600, color: palette.text2, marginBottom: 8 }}>
                    Map CSV columns to member fields
                  </div>
                  {compositeMapping.map((row, idx) => (
                    <div key={idx} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }}>
                      <select
                        value={row.csvColumn}
                        onChange={(e) => updateMapping(idx, "csvColumn", e.target.value)}
                        style={{
                          flex: 1,
                          padding: "6px 10px",
                          borderRadius: 6,
                          fontSize: FONT_SIZES.xs,
                          fontFamily: FONT_FAMILY,
                          border: `1px solid ${palette.inputBorder}`,
                          background: palette.inputBg,
                          color: palette.inputColor,
                          outline: "none",
                        }}
                      >
                        <option value="">CSV column...</option>
                        {csvHeaders.map((h) => (
                          <option key={h} value={h}>{h}</option>
                        ))}
                      </select>
                      <ArrowRight size={ICON_SIZES.sm} style={{ color: palette.text3, flexShrink: 0 }} />
                      <select
                        value={row.memberField}
                        onChange={(e) => updateMapping(idx, "memberField", e.target.value)}
                        style={{
                          flex: 1,
                          padding: "6px 10px",
                          borderRadius: 6,
                          fontSize: FONT_SIZES.xs,
                          fontFamily: FONT_FAMILY,
                          border: `1px solid ${palette.inputBorder}`,
                          background: palette.inputBg,
                          color: palette.inputColor,
                          outline: "none",
                        }}
                      >
                        {MEMBER_MATCH_FIELDS.map((f) => (
                          <option key={f.value} value={f.value}>{f.label}</option>
                        ))}
                      </select>
                      {compositeMapping.length > 1 && (
                        <button
                          onClick={() => removeMappingRow(idx)}
                          style={{ background: "none", border: "none", cursor: "pointer", padding: 2, display: "flex" }}
                        >
                          <X size={ICON_SIZES.sm} style={{ color: palette.text3 }} />
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    onClick={addMappingRow}
                    style={{
                      background: "none",
                      border: `1px dashed ${palette.cardBorder}`,
                      borderRadius: 6,
                      padding: "4px 12px",
                      fontSize: "0.6rem",
                      fontWeight: 600,
                      color: palette.text3,
                      fontFamily: FONT_FAMILY,
                      cursor: "pointer",
                      marginTop: 4,
                    }}
                  >
                    + Add field
                  </button>
                </div>
              )}

              {/* CSV preview */}
              <div>
                <div style={{ fontSize: "0.6rem", fontWeight: 600, color: palette.text2, marginBottom: 4 }}>
                  Preview (first 5 rows)
                </div>
                <div style={{ overflowX: "auto", border: `1px solid ${palette.cardBorder}`, borderRadius: 6 }}>
                  <table style={{ borderCollapse: "collapse", fontSize: "0.55rem", fontFamily: FONT_FAMILY, width: "100%" }}>
                    <thead>
                      <tr>
                        {csvHeaders.map((h) => (
                          <th
                            key={h}
                            style={{
                              padding: "6px 10px",
                              textAlign: "left",
                              fontWeight: 600,
                              color: palette.text2,
                              borderBottom: `1px solid ${palette.cardBorder}`,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {csvRows.slice(0, 5).map((row, i) => (
                        <tr key={i}>
                          {csvHeaders.map((h) => (
                            <td
                              key={h}
                              style={{
                                padding: "4px 10px",
                                color: palette.text1,
                                borderBottom: `1px solid ${palette.cardBorder}`,
                                whiteSpace: "nowrap",
                              }}
                            >
                              {row[h] || ""}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <button
                onClick={handleMatch}
                disabled={strategy === "spektrix_id" ? !spektrixColumn : !compositeMapping.some((m) => m.csvColumn)}
                style={{
                  alignSelf: "flex-end",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "8px 20px",
                  borderRadius: 8,
                  background: palette.accent1 || "#3b82f6",
                  color: "#fff",
                  fontSize: FONT_SIZES.xs,
                  fontWeight: 600,
                  fontFamily: FONT_FAMILY,
                  border: "none",
                  cursor: "pointer",
                  opacity: (strategy === "spektrix_id" ? !spektrixColumn : !compositeMapping.some((m) => m.csvColumn)) ? 0.5 : 1,
                }}
              >
                Match Recipients
                <ArrowRight size={ICON_SIZES.sm} />
              </button>
            </div>
          )}

          {/* ── STEP 3: Review ─────────────────────────────── */}
          {step === 3 && matchResult && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {/* Summary */}
              <div style={{ display: "flex", gap: 16 }}>
                <div
                  style={{
                    flex: 1,
                    padding: 16,
                    borderRadius: 8,
                    background: "#10b98110",
                    textAlign: "center",
                  }}
                >
                  <Check size={ICON_SIZES.md} style={{ color: "#10b981", marginBottom: 4 }} />
                  <div style={{ fontSize: FONT_SIZES.md, fontWeight: 700, color: "#10b981" }}>
                    {matchResult.matched.length}
                  </div>
                  <div style={{ fontSize: "0.6rem", color: palette.text2 }}>Matched</div>
                </div>
                <div
                  style={{
                    flex: 1,
                    padding: 16,
                    borderRadius: 8,
                    background: "#ef444410",
                    textAlign: "center",
                  }}
                >
                  <AlertTriangle size={ICON_SIZES.md} style={{ color: "#ef4444", marginBottom: 4 }} />
                  <div style={{ fontSize: FONT_SIZES.md, fontWeight: 700, color: "#ef4444" }}>
                    {matchResult.unmatched.length}
                  </div>
                  <div style={{ fontSize: "0.6rem", color: palette.text2 }}>Unmatched</div>
                </div>
              </div>

              {/* Unmatched list (collapsed) */}
              {matchResult.unmatched.length > 0 && (
                <details style={{ fontSize: FONT_SIZES.xs, color: palette.text2 }}>
                  <summary style={{ cursor: "pointer", fontWeight: 600, marginBottom: 8 }}>
                    Unmatched rows ({matchResult.unmatched.length})
                  </summary>
                  <div style={{ maxHeight: 200, overflowY: "auto", border: `1px solid ${palette.cardBorder}`, borderRadius: 6, padding: 8 }}>
                    {matchResult.unmatched.slice(0, 50).map((item, i) => (
                      <div key={i} style={{ fontSize: "0.55rem", color: palette.text3, padding: "2px 0" }}>
                        {Object.values(item.row).slice(0, 4).join(" | ")}
                      </div>
                    ))}
                    {matchResult.unmatched.length > 50 && (
                      <div style={{ fontSize: "0.55rem", color: palette.text3, fontStyle: "italic" }}>
                        ...and {matchResult.unmatched.length - 50} more
                      </div>
                    )}
                  </div>
                </details>
              )}

              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button
                  onClick={() => setStep(2)}
                  style={{
                    padding: "8px 16px",
                    borderRadius: 8,
                    border: `1px solid ${palette.inputBorder}`,
                    background: "transparent",
                    color: palette.text2,
                    fontSize: FONT_SIZES.xs,
                    fontWeight: 600,
                    fontFamily: FONT_FAMILY,
                    cursor: "pointer",
                  }}
                >
                  Back
                </button>
                <button
                  onClick={handleImport}
                  disabled={matchResult.matched.length === 0 || importing}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "8px 20px",
                    borderRadius: 8,
                    background: matchResult.matched.length > 0 ? "#10b981" : "#6b7280",
                    color: "#fff",
                    fontSize: FONT_SIZES.xs,
                    fontWeight: 600,
                    fontFamily: FONT_FAMILY,
                    border: "none",
                    cursor: matchResult.matched.length > 0 ? "pointer" : "default",
                    opacity: importing ? 0.6 : 1,
                  }}
                >
                  {importing ? "Importing..." : `Import ${matchResult.matched.length} Recipients`}
                </button>
              </div>
            </div>
          )}

          {/* ── STEP 4: Done ───────────────────────────────── */}
          {step === 4 && importResult && (
            <div style={{ textAlign: "center", padding: 20 }}>
              <Check size={ICON_SIZES.xxl} style={{ color: "#10b981", marginBottom: 16 }} />
              <div style={{ fontSize: FONT_SIZES.md, fontWeight: 700, color: palette.text1, marginBottom: 8 }}>
                Import Complete
              </div>
              <div style={{ fontSize: FONT_SIZES.xs, color: palette.text2 }}>
                {importResult.added} recipients added to this campaign.
              </div>
              <button
                onClick={onComplete}
                style={{
                  marginTop: 20,
                  padding: "8px 24px",
                  borderRadius: 8,
                  background: palette.accent1 || "#3b82f6",
                  color: "#fff",
                  fontSize: FONT_SIZES.xs,
                  fontWeight: 600,
                  fontFamily: FONT_FAMILY,
                  border: "none",
                  cursor: "pointer",
                }}
              >
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
